// Atlas Toolkit inside ABVME: a Spine atlas from the open bundles (atlas
// TextAsset, its page Texture2Ds and the .skel) is opened in the vendored copy
// of Atlas Toolkit (/atlas-toolkit/, an <iframe> on this origin); "Apply to
// ABVME" there replaces those assets with the edited ones.
// The message protocol is described in frontend/atlas-toolkit/js/abvme-embed.js
// and docs/atlas-toolkit.md.
import { $, escapeHtml } from "./util.js";

const TOOLKIT_URL = "/atlas-toolkit/?embed";
const IMAGE_SUFFIX = /\.(png|jpe?g|webp|bmp|tga)$/i;

const stemOf = (name) => String(name).replace(/\.[^.]+$/, "");

/** The version of the vendored Atlas Toolkit ("v0.4.1"), from the `app-version` meta of its page; "" when unknown. */
export function toolkitVersion(doc) {
  const v = doc?.querySelector('meta[name="app-version"]')?.content?.replace(/^v/i, "").trim();
  return v ? `v${v}` : "";
}

/** A TextAsset named like "hero.atlas" (Unity drops the ".txt" of "hero.atlas.txt"). */
export const isAtlasAsset = (asset) => asset?.type === "TextAsset" && /\.atlas$/i.test(asset.name);

/**
 * The atlas TextAsset for a selected asset: the atlas itself, or the atlas of
 * the same name next to a selected page texture or .skel ("hero" / "hero.skel"
 * -> "hero.atlas" in the same file).
 */
export function atlasFor(asset, assets) {
  if (!asset) return null;
  if (isAtlasAsset(asset)) return asset;
  if (asset.type !== "Texture2D" && !(asset.type === "TextAsset" && /\.skel$/i.test(asset.name))) return null;
  const want = `${asset.type === "Texture2D" ? asset.name : stemOf(asset.name)}.atlas`.toLowerCase();
  return assets.find((a) => a.file_id === asset.file_id && isAtlasAsset(a) && a.name.toLowerCase() === want) ?? null;
}

/** Page image names in atlas text: a line that is an image file name and has no ":" (Spine and libGDX formats). */
export function atlasPageNames(text) {
  const names = [];
  for (const raw of String(text).split(/\r?\n/)) {
    const line = raw.trim();
    if (line && !line.includes(":") && IMAGE_SUFFIX.test(line) && !names.includes(line)) names.push(line);
  }
  return names;
}

/** The Texture2D for an atlas page ("hero.png" -> "hero"), preferring the atlas's own file. */
export function pageTexture(pageName, atlas, assets) {
  const want = stemOf(pageName).toLowerCase();
  const textures = assets.filter((a) => a.type === "Texture2D" && a.name.toLowerCase() === want);
  return textures.find((a) => a.file_id === atlas.file_id) ?? (textures.length === 1 ? textures[0] : null);
}

/** The .skel TextAsset next to an atlas ("hero.atlas" -> "hero.skel"), if any. */
export function skelFor(atlas, assets) {
  const want = `${stemOf(atlas.name)}.skel`.toLowerCase();
  return assets.find((a) => a.file_id === atlas.file_id && a.type === "TextAsset" && a.name.toLowerCase() === want) ?? null;
}

/**
 * What the picker shows for one atlas: its pages, the pages with no texture in
 * the open files, and its .skel. It can be opened when every page has a texture;
 * without a .skel only Mesh-Aware is unavailable.
 */
export function describeAtlas(atlas, text, assets) {
  const pages = text == null ? null : atlasPageNames(text);
  const missing = pages ? pages.filter((p) => !pageTexture(p, atlas, assets)) : [];
  return { pages, missing, skel: skelFor(atlas, assets), canOpen: pages === null || (pages.length > 0 && !missing.length) };
}

/**
 * The asset's file from what the tab already holds (its pack item), or null when only an export
 * gives it: the exact bytes of a TextAsset ("raw"), or a texture whose preview is its
 * full-size PNG (a reduced preview, "scaled", is not the texture).
 */
export function localFileOf(asset, item) {
  if (!item) return null;
  if (asset.type === "TextAsset" && item.raw instanceof Blob) return new File([item.raw], asset.name);
  const p = item.preview;
  if (asset.type === "Texture2D" && p?.kind === "image" && p.scaled === false && p.mime === "image/png" && item.image instanceof Blob) {
    return new File([item.image], `${asset.name}.png`, { type: "image/png" });
  }
  return null;
}

/**
 * Exported files kept in memory, so opening the same atlas again (or another
 * atlas sharing a page) costs no export. Keyed by asset and version: a
 * replacement makes a new key. Least recently used first out over the cap.
 */
export class FileCache {
  constructor(maxBytes) {
    this.maxBytes = maxBytes;
    this.bytes = 0;
    this.map = new Map();
  }
  get(key) {
    const file = this.map.get(key);
    if (file) {
      this.map.delete(key);
      this.map.set(key, file);
    }
    return file ?? null;
  }
  set(key, file) {
    if (this.map.has(key)) this.bytes -= this.map.get(key).size;
    this.map.delete(key);
    if (file.size > this.maxBytes) return;
    this.map.set(key, file);
    this.bytes += file.size;
    for (const [k, f] of this.map) {
      if (this.bytes <= this.maxBytes) break;
      this.map.delete(k);
      this.bytes -= f.size;
    }
  }
}

const CACHE_BYTES = 256 * 1024 * 1024;
/** How long after the frame has loaded it is shown even without Atlas Toolkit's "ready". */
const REVEAL_FALLBACK_MS = 5000;

/** Save a file from Atlas Toolkit (it cannot start a download itself in Firefox, see abvme-embed.js). */
function saveBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000); // Firefox starts the download after the click returns
}

async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return out;
}

/**
 * The panel. `deps` comes from main.js:
 *   assets()                 the assets of the open bundles
 *   atlasText(asset)         the atlas's text from the local preview, or null
 *   fileName(asset)          the name of the bundle the asset is in
 *   localItem(asset)         the asset's pack item held by the tab (preview, raw bytes), or null
 *   exportFile(asset)        the asset's exported file (File), or null when cancelled
 *   version(asset)           changes when the asset is replaced (cache key)
 *   replace(asset, file)     replace an asset without asking (true when it worked)
 *   beginTask(text) / endTask(text, level)
 *   showError(title, text) / showWarning(title, text)
 *   openModal(el, opts) / closeModal(el)
 */
export function createAtlasToolkit(deps) {
  const panel = $("#atlasToolkitPanel");
  const body = panel.querySelector(".atk-body");
  const status = panel.querySelector(".atk-status");
  const version = panel.querySelector(".atk-version");
  const closeBtn = panel.querySelector(".atk-close");
  let session = null; // { atlas, pages: Map(pageName -> Texture2D), frame, files, ready }
  const cache = new FileCache(CACHE_BYTES);
  const cacheKey = (asset) => `${asset.key}:${deps.version(asset)}`;

  /** The asset's file: from the cache, else from what the tab holds, else exported (null when cancelled). */
  async function fileOf(asset) {
    const cached = cache.get(cacheKey(asset));
    if (cached) return cached;
    const local = localFileOf(asset, await deps.localItem(asset).catch(() => null));
    if (local) return local; // no export, and no need for the server's copy of the bundle
    const file = await deps.exportFile(asset);
    if (file) cache.set(cacheKey(asset), file);
    return file;
  }

  // Keep the app's status bar in view under the panel: it shows what is going on.
  const statusbar = document.querySelector(".statusbar");
  const fitAboveStatusbar = () => (panel.style.bottom = `${statusbar?.offsetHeight ?? 0}px`);
  window.addEventListener("resize", () => session && fitAboveStatusbar());

  // Pinch inside the frame must zoom the image, not this page.
  const viewport = document.querySelector('meta[name="viewport"]');
  const viewportContent = viewport?.content;
  const lockZoom = (on) => {
    if (viewport) viewport.content = on ? `${viewportContent}, maximum-scale=1, user-scalable=no` : viewportContent;
  };

  const post = (msg) => session?.frame.contentWindow?.postMessage({ abvme: 1, ...msg }, location.origin);
  const setStatus = (text) => (status.textContent = text || "");

  // The frame stays hidden until Atlas Toolkit says "ready" (its style sheets are in by then):
  // shown earlier, the page flashes unstyled. If "ready" never comes, show it anyway after a while.
  let revealTimer = 0;
  const reveal = () => {
    clearTimeout(revealTimer);
    body.classList.remove("atk-loading");
  };

  function close() {
    window.removeEventListener("message", onMessage);
    reveal();
    session?.frame.remove();
    session = null;
    panel.classList.add("d-none");
    version.textContent = "";
    document.body.classList.remove("atk-open");
    lockZoom(false);
    setStatus("");
  }

  async function onMessage(e) {
    if (!session || e.origin !== location.origin || e.source !== session.frame.contentWindow) return;
    const msg = e.data;
    if (!msg || msg.atlasToolkit !== 1) return;
    if (msg.type === "ready") {
      session.ready = true;
      reveal();
      setStatus("Loading atlas...");
      post({ type: "load", atlas: session.files.atlas, pages: session.files.pages, skel: session.files.skel });
    } else if (msg.type === "loaded") {
      setStatus("");
    } else if (msg.type === "load-failed") {
      setStatus("");
      deps.showError("Atlas Toolkit", msg.message);
    } else if (msg.type === "apply") {
      closeBtn.disabled = true; // not while the bundle is being changed
      const result = await apply(msg)
        .catch((err) => ({ ok: false, message: err.message }))
        .finally(() => (closeBtn.disabled = false));
      post({ type: "applied", id: msg.id, ...result });
      if (result.ok) {
        close();
        deps.endTask(result.message);
      }
    } else if (msg.type === "download") {
      if (msg.blob instanceof Blob && typeof msg.filename === "string") saveBlob(msg.filename, msg.blob);
    } else if (msg.type === "close" && msg.ok) {
      close();
    }
  }

  /** Put the edited atlas text and pages back into the bundle. */
  async function apply({ atlas, pages }) {
    const { atlas: atlasAsset } = session;
    const all = deps.assets();
    const targets = [];
    for (const page of pages) {
      const texture = session.pages.get(page.name) ?? pageTexture(page.name, atlasAsset, all);
      if (!texture) {
        return { ok: false, message: `The edited atlas has a page '${page.name}' that has no texture in the bundle (a bundle cannot get new textures).` };
      }
      targets.push([texture, new File([page.blob], page.name, { type: "image/png" })]);
    }
    targets.push([atlasAsset, new File([atlas.text], session.files.atlas.name, { type: "text/plain" })]);
    let done = 0;
    for (const [asset, file] of targets) {
      const text = `Applying ${++done}/${targets.length}: ${asset.name}`;
      setStatus(text);
      post({ type: "progress", text });
      if (await deps.replace(asset, file)) {
        cache.set(cacheKey(asset), file); // the new version: reopening needs no export
      } else {
        setStatus("");
        return { ok: false, message: `Replacing '${asset.name}' failed; ${done - 1} of ${targets.length} were replaced.` };
      }
    }
    setStatus("");
    const n = targets.length - 1;
    return { ok: true, message: `Atlas Toolkit: replaced ${atlasAsset.name} and ${n} page texture${n === 1 ? "" : "s"}.` };
  }

  /** Gather the atlas's files from the bundle and open the panel. */
  async function open(selected) {
    if (session) return;
    const all = deps.assets();
    const atlas = atlasFor(selected, all);
    if (!atlas) {
      deps.showWarning("Atlas Toolkit", "Select a Spine atlas (a TextAsset named *.atlas), or its page texture or .skel.");
      return;
    }
    deps.beginTask(`Preparing ${atlas.name} for Atlas Toolkit...`);
    try {
      const atlasFile = await fileOf(atlas);
      if (!atlasFile) return deps.endTask("Cancelled");
      const pageNames = atlasPageNames(await atlasFile.text());
      if (!pageNames.length) {
        deps.endTask("");
        deps.showWarning("Atlas Toolkit", `'${atlas.name}' does not look like a Spine atlas: it names no page images.`);
        return;
      }
      const pages = new Map(pageNames.map((name) => [name, pageTexture(name, atlas, all)]));
      const missing = pageNames.filter((name) => !pages.get(name));
      if (missing.length) {
        deps.endTask("");
        deps.showWarning("Atlas Toolkit", `No texture found for these pages of '${atlas.name}':\n${missing.join("\n")}\n\nOpen the bundle that holds them together with this one.`);
        return;
      }
      const skel = skelFor(atlas, all);
      const files = await mapLimit([...pages, ...(skel ? [["", skel]] : [])], 3, ([, asset]) => fileOf(asset));
      if (files.some((f) => !f)) return deps.endTask("Cancelled");
      const pageFiles = {};
      pageNames.forEach((name, i) => (pageFiles[name] = new File([files[i]], name, { type: "image/png" })));
      const skelFile = skel ? new File([files[files.length - 1]], `${stemOf(atlas.name)}.skel`) : null;
      deps.endTask(`Opened ${atlas.name} in Atlas Toolkit`);

      const frame = document.createElement("iframe");
      frame.title = "Atlas Toolkit";
      frame.src = TOOLKIT_URL;
      // Same origin: the version is stamped in the page itself, so read it from there.
      frame.addEventListener("load", () => {
        version.textContent = toolkitVersion(frame.contentDocument);
        revealTimer = setTimeout(reveal, REVEAL_FALLBACK_MS);
      });
      session = {
        atlas,
        pages,
        frame,
        files: { atlas: new File([atlasFile], atlas.name), pages: pageFiles, skel: skelFile },
      };
      window.addEventListener("message", onMessage);
      panel.querySelector(".atk-name").textContent = atlas.name;
      body.classList.add("atk-loading");
      body.replaceChildren(frame);
      panel.classList.remove("d-none");
      document.body.classList.add("atk-open");
      fitAboveStatusbar();
      lockZoom(true);
      setStatus("Starting Atlas Toolkit...");
    } catch (err) {
      deps.endTask(err.message, "error");
      deps.showError("Atlas Toolkit", err.message);
    }
  }

  // ----- picker: Tools > Atlas Toolkit -----

  const dlg = $("#atlasPickDialog");
  const list = $("#atlasPickList");
  const openBtn = $("#atlasPickOpen");
  let rows = [];
  let chosen = -1;

  function statusOf(d) {
    if (d.pages === null) return ["Not checked yet", ""];
    if (!d.pages.length) return ["No page images listed", "err"];
    if (d.missing.length) return [`Missing ${d.missing.length === 1 ? "page" : `${d.missing.length} pages`}: ${d.missing.join(", ")}`, "err"];
    const pages = `${d.pages.length} page${d.pages.length === 1 ? "" : "s"}`;
    return d.skel ? [`✓ Ready · ${pages} + .skel`, "ok"] : [`${pages} · no .skel`, "warn"];
  }

  function renderPicker() {
    list.innerHTML = rows.length
      ? rows
          .map(({ atlas, d }, i) => {
            const [text, cls] = statusOf(d);
            return `<div class="item${i === chosen ? " selected" : ""}${d.canOpen ? "" : " disabled"}" role="option" aria-selected="${i === chosen}" aria-disabled="${!d.canOpen}" data-i="${i}" tabindex="${d.canOpen ? 0 : -1}">
              <span class="name" title="${escapeHtml(atlas.name)}">${escapeHtml(atlas.name)}</span>
              <span class="meta" title="${escapeHtml(deps.fileName(atlas))}">${escapeHtml(deps.fileName(atlas))}</span>
              <span class="st ${cls}" title="${escapeHtml(text)}">${escapeHtml(text)}</span></div>`;
          })
          .join("")
      : `<div class="empty">No Spine atlas (a TextAsset named *.atlas) in the open files.</div>`;
    openBtn.disabled = !rows[chosen]?.d.canOpen;
  }

  const choose = (i) => {
    if (!rows[i]?.d.canOpen) return;
    chosen = i;
    renderPicker();
  };
  const openChosen = () => {
    const row = rows[chosen];
    if (!row?.d.canOpen) return;
    deps.closeModal(dlg);
    open(row.atlas);
  };
  list.addEventListener("click", (e) => choose(Number(e.target.closest(".item")?.dataset.i)));
  list.addEventListener("dblclick", (e) => {
    choose(Number(e.target.closest(".item")?.dataset.i));
    openChosen();
  });
  list.addEventListener("keydown", (e) => {
    if (e.key === "Enter") openChosen();
    else if (e.key === " ") {
      e.preventDefault();
      choose(Number(e.target.closest(".item")?.dataset.i));
    }
  });
  openBtn.addEventListener("click", openChosen);

  /** Show the atlases of the open files; `selected` (an atlas, page or .skel) is chosen first. */
  async function pick(selected) {
    if (session) return;
    const all = deps.assets();
    const atlases = all.filter(isAtlasAsset).sort((a, b) => a.name.localeCompare(b.name));
    rows = await Promise.all(atlases.map(async (atlas) => ({ atlas, d: describeAtlas(atlas, await deps.atlasText(atlas).catch(() => null), all) })));
    const preferred = atlasFor(selected, all);
    chosen = rows.findIndex((r) => r.atlas === preferred && r.d.canOpen);
    if (chosen < 0) chosen = rows.findIndex((r) => r.d.canOpen);
    renderPicker();
    deps.openModal(dlg, { onShown: () => (list.querySelector(".item.selected") || openBtn).focus() });
  }

  closeBtn.addEventListener("click", () => {
    // Atlas Toolkit asks first when there are changes not applied yet.
    if (session?.ready) post({ type: "close-request" });
    else close();
  });

  return { pick, open, isOpen: () => Boolean(session) };
}
